import {
  Component, Input, ViewChild, AfterViewInit, ElementRef,
  ViewChildren, Output, EventEmitter
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { FilterOption } from '../filters/filters.component';
import { Moment } from 'moment';
declare var $: any;

type Range = string;

export interface DateRangeChangeEvent {
  start: any;
  end: any;
};

@Component({
  selector: 'date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePicker implements AfterViewInit {
  @ViewChild('range') button: ElementRef;
  @ViewChildren('ranges [data-range-key]') menuItems: ElementRef;
  @Input() selectedOption: Range = 'Last 30 days';
  @Input() selectedRange: Moment[];
  @Input() start: any;
  @Input() end: any;
  @Input() buttonClass: string = 'btn-label-brand';
  // Optional offset to shift all predefined ranges by a number of years.
  // Used by the products compare date filter to show the same ranges for the previous year.
  @Input() yearOffset: number = 0;
  // Controls how preset labels are displayed. For the compare picker we want
  // text like "Last year today", "Last 7 days (last year)", etc, without
  // affecting the underlying range keys or the main picker.
  @Input() mode: 'default' | 'compare' = 'default';
  @Output() change: EventEmitter<DateRangeChangeEvent> = new EventEmitter();

  ranges: { [key: string]: Moment[] } = {};
  days: number = 0;

  constructor(private translate: TranslateService) { }

  private static readonly editableFormat = 'MM/DD/YYYY';
  // Formats accepted when parsing a hand-typed range in the editable footer.
  // Covers the natural forms people actually type: single- or double-digit
  // month/day, optional 4- or 2-digit year, or no year at all (e.g. 1/1 - 5/1,
  // which moment resolves to the current year). Without these, strict
  // MM/DD/YYYY parsing silently rejects the input and reverts the range.
  private static readonly parseFormats = ['MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY', 'M/D/YY', 'MM/DD', 'M/D'];
  // Gap kept between the popup and the viewport edges, and between the popup and the
  // button when it has to flip above it.
  private static readonly popupViewportMargin = 8;
  private static readonly popupButtonGap = 7;

  get displayLabel(): string {
    return this.selectedOption;
  }

  private makePopupSelectedEditable(drp: any) {
    if (!drp?.container) return;
    const $selected = drp.container.find('.drp-selected');
    if (!$selected?.length) return;

    const commit = () => {
      const raw = ($selected.text() || '').trim();
      const sep = (drp?.locale?.separator || ' - ').trim();
      const escapedSep = sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const datePart = '\\d{1,2}\\/\\d{1,2}(?:\\/\\d{2,4})?'; // month/day, optional 2- or 4-digit year
      const re = new RegExp(`^(${datePart})\\s*${escapedSep}\\s*(${datePart})$`);
      const match = raw.match(re);
      if (!match) {
        // revert to current picker dates
        $selected.text(drp.startDate.format(DateRangePicker.editableFormat) + (drp.locale.separator || ' - ') + drp.endDate.format(DateRangePicker.editableFormat));
        return;
      }

      const start = moment(match[1], DateRangePicker.parseFormats, true);
      const end = moment(match[2], DateRangePicker.parseFormats, true);
      if (!start.isValid() || !end.isValid()) {
        $selected.text(drp.startDate.format(DateRangePicker.editableFormat) + (drp.locale.separator || ' - ') + drp.endDate.format(DateRangePicker.editableFormat));
        return;
      }

      const s = start.isAfter(end) ? end : start;
      const e = start.isAfter(end) ? start : end;
      drp.setStartDate(s);
      drp.setEndDate(e);
      if (typeof drp.updateView === 'function') drp.updateView();
      else if (typeof drp.updateCalendars === 'function') drp.updateCalendars();
    };

    // Ensure the picker displays as MM/DD/YYYY - MM/DD/YYYY and allow typing directly in that footer field.
    $selected
      .attr('contenteditable', 'true')
      .attr('role', 'textbox')
      .attr('spellcheck', 'false')
      .off('.editableSelected')
      .on('keydown.editableSelected', (e: any) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      })
      .on('blur.editableSelected', () => commit());
  }

  // bootstrap-daterangepicker places its popup absolutely on <body> and always drops
  // *down* from the button without checking the viewport (its own `drops: 'auto'` compares
  // the popup against body scrollHeight rather than the visible area, so on a long page it
  // never flips). When the button sits low on the screen - the Activities filter popover on
  // the product page is the worst case - the whole popup lands below the fold and cannot be
  // reached. Wrap move() so that every reposition it makes (opening, expanding Custom Range,
  // window resize) is followed by a nudge back into view. Overlapping whatever opened the
  // picker is fine; being off-screen is not.
  private keepPopupOnScreen(drp: any) {
    if (!drp || drp.keptOnScreen) return;
    const originalMove = drp.move.bind(drp);
    drp.move = (...args: any[]) => {
      originalMove(...args);
      this.nudgeIntoViewport(drp);
    };
    drp.keptOnScreen = true;
  }

  private nudgeIntoViewport(drp: any) {
    const popup: HTMLElement = drp?.container?.[0];
    const button: HTMLElement = drp?.element?.[0];
    // move() also runs while the popup is still hidden, when there is nothing to measure.
    if (!popup || !button || !popup.offsetHeight) return;

    const margin = DateRangePicker.popupViewportMargin;
    const gap = DateRangePicker.popupButtonGap;
    const buttonRect = button.getBoundingClientRect();
    let rect = popup.getBoundingClientRect();

    // Vertical: prefer flipping above the button; if the popup does not fit there either
    // (short window), pin it inside the viewport instead.
    let shiftY = 0;
    let placedAbove = false;
    if (rect.bottom > window.innerHeight - margin) {
      const above = buttonRect.top - rect.height - gap;
      placedAbove = above >= margin;
      const target = placedAbove ? above : Math.max(margin, window.innerHeight - margin - rect.height);
      shiftY = target - rect.top;
    } else if (rect.top < margin) {
      shiftY = margin - rect.top;
    }
    if (shiftY) {
      popup.style.top = `${(parseFloat(popup.style.top) || 0) + shiftY}px`;
      drp.container.toggleClass('drop-up', placedAbove);
    }

    // Horizontal: move() pins either left or right depending on `opens`, so shift the one
    // it actually used and leave the other on auto.
    rect = popup.getBoundingClientRect();
    let shiftX = 0;
    if (rect.right > window.innerWidth - margin) shiftX = (window.innerWidth - margin) - rect.right;
    if (rect.left + shiftX < margin) shiftX = margin - rect.left;
    if (shiftX) {
      if (popup.style.right && popup.style.right !== 'auto')
        popup.style.right = `${(parseFloat(popup.style.right) || 0) - shiftX}px`;
      else
        popup.style.left = `${(parseFloat(popup.style.left) || 0) + shiftX}px`;
    }
  }

  private buildRanges() {
    const today = this.today();
    if (this.mode === 'compare') {
      this.ranges = {
        'Today (previous year)': [today.clone(), today.clone()],
        'Yesterday (previous year)': [today.clone().subtract(1, 'days'), today.clone()],
        'Last 7 days (previous year)': [today.clone().subtract(6, 'days'), today.clone()],
        'Last 30 days (previous year)': [today.clone().subtract(29, 'days'), today.clone()],
        'Year before last': [today.clone().subtract(365, 'days'), today.clone()],
        'All data (previous year)': [moment('2015-01-01'), today.clone()],
      };
    } else {
      this.ranges = {
        'Today': [today.clone(), today.clone()],
        'Yesterday': [today.clone().subtract(1, 'days'), today.clone()],
        'Last 7 days': [today.clone().subtract(6, 'days'), today.clone()],
        'Last 30 days': [today.clone().subtract(29, 'days'), today.clone()],
        'Last Year': [today.clone().subtract(365, 'days'), today.clone()],
        'All': [moment('2015-01-01'), today.clone()],
      };
    }
  }

  ngAfterViewInit() {
    // Build ranges using the (optionally offset) "today" for this instance.
    this.buildRanges();
    if (this.start && this.end) {
      let matched = false;
      for (let range of Object.keys(this.ranges)) {
        if (this.ranges[range][0].format('YYYY-MM-DD') == this.start &&
          this.ranges[range][1].format('YYYY-MM-DD') == this.end) {
          this.selectedOption = range as any;
          matched = true;
          break;
        }
      }
      if (!matched)
        this.selectedOption = this.start + ' to ' + this.end as any;
    }
    else {
      // When no explicit start/end are provided, fall back to a safe preset.
      // Guard against cases where selectedOption is not a valid key in ranges
      // (e.g. when using compare mode with default "Last 30 days").
      const labels = Object.keys(this.ranges);
      const hasSelected = labels.includes(this.selectedOption);
      const effectiveKey = hasSelected ? this.selectedOption : labels[0];
      const baseRange = this.ranges[effectiveKey];
      this.selectedOption = effectiveKey as any;
      this.start = this.selectedRange ? this.selectedRange[0] : baseRange[0].format('YYYY-MM-DD');
      this.end = this.selectedRange ? this.selectedRange[1] : baseRange[1].format('YYYY-MM-DD');
    }
    $(this.button.nativeElement).daterangepicker({
      buttonClasses: ' btn',
      applyClass: 'btn-primary',
      cancelClass: 'btn-secondary',
      ranges: this.ranges,
      locale: {
        format: DateRangePicker.editableFormat
      }
    }, (start, end, label) => this.setDates(start, end, label));
    this.keepPopupOnScreen($(this.button.nativeElement).data('daterangepicker'));

    // Make the popup "selected range" footer field editable (the one next to Apply/Cancel).
    $(this.button.nativeElement)
      .off('show.daterangepicker.editableSelected')
      .on('show.daterangepicker.editableSelected', (_ev: any, drp: any) => this.makePopupSelectedEditable(drp));
    if (this.selectedRange) {
      let labels = Object.keys(this.ranges);
      for (let label of labels) {
        if (this.ranges[label][0].isSame(this.selectedRange[0]) && this.ranges[label][1].isSame(this.selectedRange[1])) {
          this.selectedOption = <any>label;
          break;
        }
      }
    }
    else
      this.selectedRange = this.ranges[this.selectedOption];
    this.setDates(moment(this.start), moment(this.end), this.selectedOption, true);
  }

  today() {
    const base = moment(moment().utc().format('YYYY-MM-DDT00:00:00+00:00')).utc();
    return this.yearOffset ? base.add(this.yearOffset, 'year') : base;
  }

  next() {
    let labels = Object.keys(this.ranges);
    let index = labels.indexOf(this.selectedOption) + 1;
    if (index < labels.length) {
      let range = this.ranges[labels[index]];
      this.setDates(range[0], range[1], labels[index]);
    }
    else
      this.button.nativeElement.click();
  }

  previous() {
    let labels = Object.keys(this.ranges);
    let index = labels.indexOf(this.selectedOption);
    index = (index < 0 ? labels.length : index) - 1;
    let range = this.ranges[labels[index]];
    this.setDates(range[0], range[1], labels[index]);
  }

  setDates(start, end, label, skipChange = false) {
    let labels = Object.keys(this.ranges);
    let notCustom: boolean = labels.includes(label);
    this.selectedOption = notCustom ? label : start.format('YYYY-MM-DD') + ' to ' + end.format('YYYY-MM-DD');
    this.start = start.format('YYYY-MM-DD');
    this.end = end.format('YYYY-MM-DD');
    this.days = end.diff(start, 'days') + 1;
    const drp: any = $(this.button.nativeElement).data('daterangepicker');
    if (drp) {
      drp.setStartDate(start);
      drp.setEndDate(end);
      // Ensure the popup highlights the correct preset instead of "Custom Range"
      drp.chosenLabel = notCustom ? this.selectedOption : 'Custom Range';
    }
    if (!skipChange)
      this.change.emit({ start: this.start, end: this.end });
  }

  totalDays() {
    return this.days;
  }

  get filterOrderDate(): FilterOption {
    return {
      type: 'Between',
      value: this.start,
      value2: this.end
    } as FilterOption;
  }
}
